import { createSlice } from "@reduxjs/toolkit";
import { AibleClientFactory } from "client/aible/factory";
import { RecordType } from "types/base";
import { getSelectedAccountType } from "containers/Accounts/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import * as foldersReducer from "containers/Folders/reducer";
import * as accountsReducer from "containers/Accounts/reducer";
import * as globalReducer from "containers/App/reducer";
import * as loginReducer from "containers/Login/reducer";
import { readCSV } from "containers/Deployments/reducer";
import { toast } from "react-toastify";
import { isEnabled } from "utils/featureFlags";
import { MODELS } from "containers/ChatGPT/reducer";
const ARTIFACT_CLIENT = AibleClientFactory.getInstance().getLanguageModelArtifactClient();
const initialState = {
    CORPUS: {},
    FINE_TUNED_MODEL: {},
    FINE_TUNING_DATA: {}
};
export const GEMINI_FINE_TUNING_OPTION = {
    value: "gemini-1.5-flash-002",
    label: "Gemini 1.5 Flash"
};
function updateStateWithArtifact(state, artifact) {
    return {
        ...state,
        [artifact.type]: {
            ...(state[artifact.type] ?? {}),
            [artifact.id]: artifact
        }
    };
}
const artifactSlice = createSlice({
    name: "language_model_artifact",
    initialState: initialState,
    reducers: {
        updateArtifact(state, { payload }) {
            return updateStateWithArtifact(state, payload);
        },
        updateArtifacts(state, { payload }) {
            return payload.reduce(updateStateWithArtifact, state);
        },
        deleteFineTuningDataset(state, { payload }) {
            const updatedFineTuningData = { ...state["FINE_TUNING_DATA"] };
            delete updatedFineTuningData[payload.id];
            const update = {
                ...state,
                ["FINE_TUNING_DATA"]: updatedFineTuningData
            };
            return update;
        }
    }
});
export const { updateArtifact, updateArtifacts } = artifactSlice.actions;
export default artifactSlice.reducer;
// Reducing minimum to 0 for testing, planned 50 for production
export const FEEDBACK_BREAKPOINTS = {
    MIN: 20,
    RECOMMENDED: 1000
};
function getBase(state) {
    return state.languageModelArtifact;
}
export function getArtifact(state, type, id) {
    return getBase(state)?.[type]?.[id];
}
export function getArtifactList(state, type, folderOverride) {
    const retFolderId = folderOverride ?? foldersReducer.getSelectedFolder(state);
    return Object.values(getBase(state)[type])
        .filter((t) => {
        if (t?.folder_id !== retFolderId) {
            return false;
        }
        return t.deleted == RecordType.NotDeleted;
    })
        .sort((a, b) => b.id - a.id);
}
export function getCorpusList(state, folderOverride) {
    const retFolderId = folderOverride ?? foldersReducer.getSelectedFolder(state);
    const corpusList = getArtifactList(state, "CORPUS", retFolderId).filter((c) => {
        const cFolder = c?.folder_id;
        return cFolder === retFolderId;
    });
    return corpusList;
}
export function getFineTuneBaseModelOptions(state) {
    const BASE_FINE_TUNED_MODELS = [
        MODELS.GEMINI_1_5
        // MODELS.MISTRAL
    ];
    const modelOptions = chatGPTReducer.getModelOptions(state);
    const retOptions = modelOptions.filter((o) => {
        if (BASE_FINE_TUNED_MODELS.find((m) => o.value === m)) {
            return true;
        }
        return false;
    });
    return retOptions;
}
export function getFineTunedModelList(state) {
    //Temporary code for marketplace demo that's storing items in localstorage.
    const modelList = getArtifactList(state, "FINE_TUNED_MODEL");
    if (isEnabled("community-market")) {
        const purschasedString = localStorage.getItem("market_purchases");
        const selectedAccountId = accountsReducer.getSelectedAccountId(state);
        const selectedTenant = loginReducer.getSelectedTenant(state);
        const selectedFolderId = foldersReducer.getSelectedFolder(state);
        let retModels = [];
        if (purschasedString !== null) {
            const parsedPurchases = JSON.parse(purschasedString);
            const parsedModels = parsedPurchases?.filter((item) => item?.type === "FINE_TUNED_MODEL");
            retModels = parsedModels?.filter((model) => model?.account_id === selectedAccountId &&
                model?.tenant_id === selectedTenant?.id &&
                model?.folder_id === selectedFolderId);
        }
        return [...retModels, ...modelList];
    }
    // End of temporary code
    return getArtifactList(state, "FINE_TUNED_MODEL");
}
export function getFineTunedModels(state) {
    if (!isEnabled("model-fine-tuning")) {
        return [];
    }
    const fineTunedModelList = getFineTunedModelList(state);
    const readyModels = fineTunedModelList.filter((m) => {
        if (m?.metadata?.status === "INPROGRESS") {
            return false;
        }
        return m?.metadata?.status === "COMPLETE";
    });
    return readyModels;
}
export function getInProgressFineTunedModels(state) {
    const fineTunedModelList = getFineTunedModelList(state);
    const inProgressModels = fineTunedModelList.filter((m) => {
        return m?.metadata?.status === "INPROGRESS";
    });
    return inProgressModels;
}
export function pollFineTunedModels() {
    return (dispatch, getState) => {
        const inProgressModels = getInProgressFineTunedModels(getState());
        if (!inProgressModels?.length) {
            return Promise.all([]);
        }
        const POLL_MAX = Math.min(5, inProgressModels.length);
        for (let i = inProgressModels.length - 1; i >= POLL_MAX; i--) {
            const currIndex = Math.floor(Math.random() * inProgressModels.length);
            inProgressModels.splice(currIndex, 1);
        }
        return Promise.all(inProgressModels.map((m) => {
            return dispatch(fetchFineTunedModel(m.id, true)).then((res) => {
                if (m?.metadata?.status !== res?.metadata?.status) {
                    dispatch(updateArtifact(res));
                }
                return res;
            });
        }));
    };
}
export function getFineTuningDataList(state) {
    if (!isEnabled("model-fine-tuning")) {
        return [];
    }
    return getArtifactList(state, "FINE_TUNING_DATA");
}
export function getCorpus(state, id) {
    return getArtifact(state, "CORPUS", id);
}
export function getCorpusParameters(state, id) {
    return getCorpus(state, id)?.metadata.parameters;
}
export function checkAllowCorpusModelSelect(processorType) {
    if (!isEnabled("corpus-model-select")) {
        return false;
    }
    return processorType !== "nvingest";
}
export function getCorpusDocumentList(state, id) {
    return getCorpus(state, id)?.metadata.document_list ?? [];
}
export function getFineTunedModel(state, id) {
    return getArtifact(state, "FINE_TUNED_MODEL", id);
}
export function getFineTuningData(state, id) {
    return getArtifact(state, "FINE_TUNING_DATA", id);
}
export function getIsCorpusLoading(state, artifactId) {
    const status = getCorpus(state, artifactId)?.metadata?.status;
    if (status === "INPROGRESS") {
        return true;
    }
    return false;
}
export function fetchArtifacts(accountId, folderId, type) {
    return async (dispatch, getState) => {
        const artifacts = await ARTIFACT_CLIENT.getArtifacts({
            account_id: accountId,
            folder_id: folderId,
            type: type
        });
        dispatch(artifactSlice.actions.updateArtifacts(artifacts));
        return getArtifactList(getState(), type);
    };
}
export function createArtifact(accountId, folderId, type, name, metadata) {
    return (dispatch) => {
        return ARTIFACT_CLIENT.createArtifact(accountId, {
            folder_id: folderId,
            type: type,
            name: name,
            metadata: metadata
        }).then((artifact) => {
            dispatch(artifactSlice.actions.updateArtifact(artifact));
            return artifact;
        });
    };
}
export function createFineTunedModelOne(fArtifact, accountId, folderId, type, name) {
    return (dispatch) => {
        return ARTIFACT_CLIENT.createArtifact(accountId, {
            folder_id: folderId,
            type: "FINE_TUNED_MODEL",
            name: name
        }).then((artifact) => {
            dispatch(artifactSlice.actions.updateArtifact(artifact));
            console.log("are we here model artifact?", artifact);
            dispatch(trainFineTunedModel(fArtifact, artifact.id + "", artifact.name + "_trained"));
            return artifact;
        });
    };
}
export function fetchFineTunedModelList(accountId, folderId) {
    return fetchArtifacts(accountId, folderId, "FINE_TUNED_MODEL");
}
export function fetchFineTuningDataList(accountId, folderId) {
    return fetchArtifacts(accountId, folderId, "FINE_TUNING_DATA");
}
export function fetchCorpusList(accountId, folderId) {
    return fetchArtifacts(accountId, folderId, "CORPUS");
}
export function createCorpus(accountId, folderId, name, modelId, processorType) {
    const metadata = {};
    const parameters = {
        model: modelId,
        processor_type: "default" // setting the processor_type to default
    };
    const hasProcessorType = isEnabled("document-set-processor-type") && processorType;
    if (hasProcessorType) {
        parameters.processor_type = processorType;
    }
    metadata.parameters = parameters;
    return createArtifact(accountId, folderId, "CORPUS", name, metadata);
}
export function createFineTunedModel(accountId, folderId, name) {
    return createArtifact(accountId, folderId, "FINE_TUNED_MODEL", name);
}
export function createFineTuningData(accountId, folderId, name) {
    return createArtifact(accountId, folderId, "FINE_TUNING_DATA", name);
}
export function fetchAllArtifacts(folderIdOverride, filter) {
    return (dispatch, getState) => {
        const folderId = folderIdOverride ?? foldersReducer.getSelectedFolder(getState());
        const accounts = accountsReducer.getActiveAccounts(getState());
        dispatch(globalReducer.updateIsArtifactsLoading(true));
        return Promise.all(accounts.map((account) => {
            return ARTIFACT_CLIENT.getArtifactsWithDetails(account.id, folderId, filter)
                .then((artifacts) => {
                dispatch(updateArtifacts(artifacts));
                return artifacts;
            })
                .catch((e) => {
                console.log(e);
                return [];
            });
        })).then((allArtifacts) => {
            dispatch(globalReducer.updateIsArtifactsLoading(false));
            return allArtifacts.flat();
        });
    };
}
export function fetchArtifact(type, artifactId) {
    return (dispatch, getState) => {
        return ARTIFACT_CLIENT.getArtifact(artifactId)
            .then((artifact) => {
            dispatch(updateArtifact(artifact));
            const persisted = getArtifact(getState(), type, artifactId);
            if (persisted === undefined) {
                throw Error("not persisted!");
            }
            return persisted;
        })
            .then((artifact) => {
            if (type === "CORPUS") {
                return dispatch(fetchCorpusDetails(artifactId)).then(() => {
                    const persisted = getArtifact(getState(), type, artifactId);
                    if (persisted === undefined) {
                        throw Error("not persisted!");
                    }
                    return persisted;
                });
            }
            return artifact;
        });
    };
}
export function fetchFineTunedModel(artifactId, skipUpdate = false) {
    return (dispatch, getState) => {
        const artifact = getFineTunedModel(getState(), artifactId);
        if (artifact === undefined) {
            throw Error("Fine Tuned Model not found");
        }
        return ARTIFACT_CLIENT.getFineTunedModel(artifact).then((model) => {
            if (skipUpdate) {
                return model;
            }
            dispatch(updateArtifact(model));
            const persisted = getFineTunedModel(getState(), artifactId);
            if (persisted === undefined) {
                throw Error("not persisted!");
            }
            return persisted;
        });
    };
}
export function fetchFineTuningData(artifactId) {
    return (dispatch) => {
        return dispatch(fetchArtifact("FINE_TUNING_DATA", artifactId)).then((artifact) => {
            return artifact;
        });
    };
}
// Doesn't seem to work. Unknown if due to account update issues;
// export function fetchFineTuningData(artifactId: number, skipUpdate = false): BaseThunk<Promise<FineTuningData>> {
//     return (dispatch, getState) => {
//         const artifact = getFineTuningData(getState(), artifactId);
//         if (artifact === undefined) {
//             throw Error("Fine Tuned Model not found")
//         }
//         return ARTIFACT_CLIENT.getFineTuningData(artifact).then(
//             (fineTuningData) => {
//                 if (skipUpdate) {
//                     return fineTuningData;
//                 }
//                 dispatch(updateArtifact(fineTuningData));
//                 const persisted = getFineTuningData(getState(), artifactId);
//                 if (persisted === undefined) {
//                     throw Error("not persisted!")
//                 }
//                 return persisted;
//             }
//         )
//     }
// }
export function fetchCorpus(artifactId) {
    return (dispatch) => {
        return dispatch(fetchArtifact("CORPUS", artifactId)).then(() => {
            return dispatch(fetchCorpusDetails(artifactId));
        });
    };
}
export function updateArtifactAndSave(artifact, update) {
    return (dispatch) => {
        return ARTIFACT_CLIENT.updateArtifact(artifact, update).then((artifact) => {
            dispatch(updateArtifact(artifact));
            return artifact;
        });
    };
}
export function deleteArtifact(artifactId) {
    return (dispatch) => {
        return ARTIFACT_CLIENT.deleteArtifact(artifactId).then((artifact) => {
            dispatch(updateArtifact(artifact));
            return artifact;
        });
    };
}
export function deleteCorpus(artifactId) {
    return (dispatch, getState) => {
        const corpus = getCorpus(getState(), artifactId);
        const selectedAccountType = getSelectedAccountType(getState()) || "cloud";
        const shouldDelete = confirm("After removing this card Aible will not be able to summarize " +
            (corpus ? corpus.name : "Document Set " + artifactId) +
            " unless you re-upload the documents. Aible cannot delete documents from your " +
            selectedAccountType +
            " account(s), " +
            "so this action will not delete the actual documents. If you wish to remove the documents, " +
            "you will need to delete it from your " +
            selectedAccountType +
            " account.");
        if (shouldDelete) {
            return dispatch(deleteArtifact(artifactId));
        }
        return Promise.resolve();
    };
}
export function uploadArtifactAsset(artifact, file) {
    return () => ARTIFACT_CLIENT.uploadArtifactAsset(artifact, file);
}
export function addFineTuningData(artifact, records) {
    return (dispatch) => ARTIFACT_CLIENT.addFineTuningData(artifact, records).then(() => {
        return dispatch(fetchFineTuningData(artifact.id)).then((res) => {
            console.log(res);
        });
    });
}
export function createTrainingDataset(artifact, data) {
    return (dispatch) => ARTIFACT_CLIENT.createTrainingDataset(artifact, data).then(() => {
        return dispatch(fetchFineTuningData(artifact.id)).then((res) => {
            return res;
        });
    });
}
export function deleteFineTuningDataset(artifact) {
    return (dispatch) => ARTIFACT_CLIENT.deleteFineTuningDataset(artifact).then((res) => {
        dispatch(artifactSlice.actions.deleteFineTuningDataset(artifact));
        console.log(res);
    });
}
export function trainFineTunedModel(artifact, baseModelId, name, options) {
    return (dispatch) => {
        // Rename Mistral model
        let retModelId = baseModelId;
        if (baseModelId === MODELS.MISTRAL) {
            retModelId = "mistral";
        }
        const retOptions = {
            base_model_id: retModelId,
            display_name: name
        };
        if (options) {
            retOptions.parameters = options;
        }
        return ARTIFACT_CLIENT.createFineTunedModel(artifact, retOptions).then((response) => {
            dispatch(updateArtifact(response));
            return response;
        });
    };
}
function parseFineTuningFile(file) {
    return readCSV(file)
        .then((csvArray) => {
        const records = [];
        csvArray.forEach((row) => {
            const currRecord = {
                input_text: row[0],
                output_text: row[1]
            };
            if (row[2] && !isNaN(Number(row[2]))) {
                currRecord.feedback_score = Number(row[2]);
            }
            records.push(currRecord);
        });
        return records;
    })
        .catch((e) => {
        console.log(e);
        toast(`There was an error reading the csv. Please ensure that it is formatted correctly and try again.`, {
            type: toast.TYPE.ERROR,
            position: toast.POSITION.TOP_CENTER,
            autoClose: 7500
        });
        throw e;
    });
}
export function addCustomDataToFineTuningData(artifact, file) {
    return (dispatch, getState) => {
        return parseFineTuningFile(file).then((records) => {
            return dispatch(addFineTuningData(artifact, records)).then(() => {
                // This is not fetching the details
                return dispatch(fetchFineTuningData(artifact.id));
            });
        });
    };
}
export function uploadCustomData(name, file, accountId) {
    return (dispatch, getState) => {
        const folderId = foldersReducer.getSelectedFolder(getState());
        return dispatch(createFineTuningData(accountId, folderId, name)).then((artifact) => {
            dispatch(updateArtifact(artifact));
            return dispatch(addCustomDataToFineTuningData(artifact, file));
        });
    };
}
export function uploadCustomModel(baseModelId, name, file, accountId) {
    return (dispatch, getState) => {
        // Rename Mistral model
        if (baseModelId === MODELS.MISTRAL) {
            baseModelId = "mistral";
        }
        const folderId = foldersReducer.getSelectedFolder(getState());
        const datasetClient = AibleClientFactory.getInstance().getDatasetClient();
        return datasetClient.uploadFile(file, accountId).then((url) => {
            return ARTIFACT_CLIENT.createArtifact(accountId, {
                folder_id: folderId,
                name: name,
                type: "FINE_TUNED_MODEL",
                metadata: {
                    status: "COMPLETE",
                    base_model_id: baseModelId,
                    fine_tuning_data_id: -1,
                    tuning_output: {
                        type: "LLAMACPP_LORA",
                        lora_out_uri: url
                    }
                }
            }).then((response) => dispatch(fetchArtifact("FINE_TUNED_MODEL", response.id)));
        });
    };
}
export function trainFineTunedModelTest(artifact, baseModelId, name) {
    return (dispatch) => {
        console.log(artifact, baseModelId, name);
        const newTest = new Promise((resolve, reject) => {
            setTimeout(() => {
                const testModel = {
                    base_model_id: MODELS.CHAT_GPT,
                    name: "OpenAI GPT-4",
                    description: "Some description that the user adds for this model. It could be useful for Mistral uses",
                    created_by: { id: 0, fullname: "test" },
                    created_date: "2024-01-08T09:54:05.733Z",
                    status: "INPROGRESS"
                };
                resolve(testModel);
            }, 100);
        });
        return newTest.then((response) => {
            return response;
        });
    };
}
export function cancelFineTuning(artifact) {
    return (dispatch) => {
        return ARTIFACT_CLIENT.cancelFineTuning(artifact).then((artifact) => {
            dispatch(updateArtifact(artifact));
            return artifact;
        });
    };
}
export function addCorpusDocument(artifact, file) {
    return (dispatch) => ARTIFACT_CLIENT.uploadArtifactAsset(artifact, file).then(() => ARTIFACT_CLIENT.reindexCorpus(artifact).then((retArtifact) => {
        dispatch(updateArtifact(retArtifact));
    }));
}
export function reindexCorpus(artifact) {
    return (dispatch) => ARTIFACT_CLIENT.reindexCorpus(artifact).then((retArtifact) => {
        console.log("Reindex and fetching");
        dispatch(updateArtifact(retArtifact));
    });
}
export function addCorpusDocuments(artifact, files) {
    return (dispatch, getState) => {
        const folderId = foldersReducer.getSelectedFolder(getState());
        const MAX_CALLS = 50;
        const errors = [];
        const corpusUploadHelper = (uFiles) => {
            if (uFiles.length <= 0) {
                return ARTIFACT_CLIENT.reindexCorpus(artifact)
                    .then((retArtifact) => {
                    if (errors.length) {
                        console.log(errors);
                        toast(`There was an error uploading ${errors.length} file${errors.length === 1 ? "" : "s"}:\n${errors.join("\n")}\nPlease try uploading ${errors.length === 1 ? "it" : "these files"} again.`, {
                            type: toast.TYPE.ERROR,
                            position: toast.POSITION.TOP_CENTER,
                            autoClose: false
                        });
                    }
                    const currFolderId = foldersReducer.getSelectedFolder(getState());
                    if (folderId === currFolderId) {
                        return dispatch(updateArtifact(retArtifact));
                    }
                })
                    .then(() => {
                    return Promise.resolve();
                });
            }
            const currUpload = uFiles.slice(0, MAX_CALLS);
            const remainingUploads = uFiles.slice(MAX_CALLS);
            return Promise.all(currUpload.map((file) => {
                return ARTIFACT_CLIENT.uploadArtifactAsset(artifact, file)
                    .then((r) => {
                    return r;
                })
                    .catch((e) => {
                    console.log(e);
                    errors.push(file.name);
                });
            }))
                .then(() => {
                return corpusUploadHelper(remainingUploads);
            })
                .catch(() => {
                return corpusUploadHelper(remainingUploads);
            });
        };
        return corpusUploadHelper(files);
    };
}
export function uploadDocumentUrls(artifact, urls) {
    return (dispatch) => {
        return ARTIFACT_CLIENT.addDocumentUrls(artifact, urls)
            .then(() => {
            return dispatch(fetchCorpus(artifact.id));
        })
            .catch((e) => {
            console.log(e);
            return dispatch(fetchCorpus(artifact.id));
        });
    };
}
export function applyCorpusChunkSizeOptions(artifactId, parameters) {
    return async (dispatch, getState) => {
        const artifact = getCorpus(getState(), artifactId);
        if (artifact === undefined) {
            return Promise.resolve(artifact);
        }
        const update = {
            metadata: {
                ...artifact.metadata,
                parameters: {
                    ...artifact.metadata.parameters,
                    ...parameters
                }
            }
        };
        await dispatch(updateArtifactAndSave(artifact, update));
        return ARTIFACT_CLIENT.reindexCorpus(artifact).then((retArtifact) => {
            dispatch(updateArtifact(retArtifact));
            return retArtifact;
        });
    };
}
export function updateCorpusParameters(artifactId, parameters) {
    return (dispatch, getState) => {
        const artifact = getCorpus(getState(), artifactId);
        if (artifact === undefined) {
            return artifact;
        }
        const update = {
            ...artifact,
            metadata: {
                ...artifact.metadata,
                parameters: parameters
            }
        };
        dispatch(updateArtifact(update));
    };
}
export function updateIsCorpusLoading(artifactId, value) {
    const status = value === true ? "INPROGRESS" : "COMPLETE";
    return (dispatch, getState) => {
        const artifact = getCorpus(getState(), artifactId);
        if (artifact == undefined) {
            return artifact;
        }
        const retMetadata = {
            ...(artifact.metadata ?? {}),
            status: status
        };
        const retArtifact = {
            ...artifact,
            metadata: retMetadata
        };
        dispatch(updateArtifact(retArtifact));
        return retArtifact;
    };
}
export function updateCorpusDocumentList(artifactId, value) {
    return (dispatch, getState) => {
        const artifact = getCorpus(getState(), artifactId);
        if (artifact === undefined) {
            return artifact;
        }
        const retMetadata = {
            ...(artifact.metadata ?? {}),
            document_list: value
        };
        const retArtifact = {
            ...artifact,
            metadata: retMetadata
        };
        dispatch(updateArtifact(retArtifact));
        return retArtifact;
    };
}
export function fetchCorpusDetails(artifactId) {
    return (dispatch, getState) => {
        const artifact = getCorpus(getState(), artifactId);
        if (artifact === undefined) {
            throw Error("Corpus not found");
        }
        return ARTIFACT_CLIENT.getCorpusDocumentList(artifact).then((resDocs) => {
            dispatch(updateCorpusDocumentList(artifactId, resDocs));
            const persisted = getCorpus(getState(), artifactId);
            if (persisted === undefined) {
                throw Error("not persisted!");
            }
            return persisted;
        });
    };
}
